import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BiArrowBack } from "react-icons/bi";
import Container from "../components/Container";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { base_url, config } from "../utils/axiosConfig";
import {
  createAnOrder,
  deleteUserCart,
  getUserCart,
  resetState,
} from "../features/user/userSlice";

let shippingSchema = yup.object({
  firstname: yup.string().required("First Name is Required"),
  lastname: yup.string().required("Last Name is Required"),
  address: yup.string().required("Address Details are Required"),
  state: yup.string().required("State is Required"),
  city: yup.string().required("City is Required"),
  country: yup.string().required("Country is Required"),
  pincode: yup.number("Pincode No is Required").required().positive().integer(),
});

const Checkout = () => {
  const dispatch = useDispatch();
  const cartState = useSelector((state) => state?.auth?.cartProducts);
  const authState = useSelector((state) => state?.auth);
  const [totalAmount, setTotalAmount] = useState(null);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let sum = 0;
    for (let index = 0; index < cartState?.length; index++) {
      sum = sum + Number(cartState[index].quantity) * cartState[index].price;
      setTotalAmount(sum);
    }
  }, [cartState]);

  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config2 = useMemo(() => {
    const getTokenFromLocalStorage = localStorage.getItem("customer")
      ? JSON.parse(localStorage.getItem("customer"))
      : null;

    return {
      headers: {
        Authorization: `Bearer ${getTokenFromLocalStorage !== null
          ? getTokenFromLocalStorage.token
          : ""
          }`,
        Accept: "application/json",
      },
    };
  }, [
    localStorage.getItem("customer")
      ? JSON.parse(localStorage.getItem("customer"))?.token
      : null,
  ]); // Dependency on token

  useEffect(() => {
    dispatch(getUserCart(config2));
  }, [dispatch, config2]);

  useEffect(() => {
    if (
      authState?.orderedProduct?.order !== null &&
      authState?.orderedProduct?.success === true
    ) {
      navigate("/my-orders");
    }
  }, [authState, navigate]);

  const [cartProductState, setCartProductState] = useState([]);

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      address: "",
      state: "",
      city: "",
      country: "",
      pincode: "",
      other: "",
    },
    validationSchema: shippingSchema,
    onSubmit: (values) => {
      setShippingInfo(values);
      localStorage.setItem("address", JSON.stringify(values));
      setTimeout(() => {
        initiatePhonePePayment();
      }, 300);
    },
  });

  useEffect(() => {
    let items = [];
    for (let index = 0; index < cartState?.length; index++) {
      items.push({
        product: cartState[index].productId._id,
        quantity: cartState[index].quantity,
        color: cartState[index].color._id,
        price: cartState[index].price,
        variants: cartState[index].variants,
      });
    }
    setCartProductState(items);
  }, [cartState]);

  const handleApplyCoupon = async () => {
    if (!coupon) return;
    try {
      const response = await axios.post(
        `${base_url}user/cart/apply-coupon`,
        { coupon },
        config2
      );
      if (response.data) {
        setAppliedDiscount(response.data);
        setPaymentError(null);
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setPaymentError(error.response?.data?.message || "Invalid or Expired Coupon");
      setAppliedDiscount(null);
    }
  };



  // **Step 1: Initiate Payment**
  const initiatePhonePePayment = async () => {
    try {
      const response = await axios.post(
        `${base_url}user/order/checkout`,
        {
          amount: totalAmount + 100, // Including extra charges if any
          name: `${formik.values.firstname} ${formik.values.lastname}`,
          mobileNumber: "9999999999", // Replace with actual user mobile number
          shippingInfo: JSON.parse(localStorage.getItem("address")),
          orderItems: cartProductState,
          totalPrice: totalAmount,
          totalPriceAfterDiscount: appliedDiscount ? appliedDiscount : totalAmount,
        },
        config
      );

      if (response.data && response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl; // Redirect to PhonePe payment page
      } else {
        setPaymentError("Failed to initiate PhonePe payment.");
      }
    } catch (error) {
      console.error("Error initiating PhonePe payment:", error);
      setPaymentError(`Something went wrong with PhonePe: ${error.message}`);
      if (error.response) {
        console.error("Server Response:", error.response);
      }
    }
  };

  const [isVerifying, setIsVerifying] = useState(false);

  // **Step 2: Verify Payment (After Redirection)**
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const merchantTransactionId = urlParams.get("merchantTransactionId"); // Our Order ID

    if (merchantTransactionId && cartProductState.length > 0 && totalAmount !== null && !isVerifying) {
      setIsVerifying(true);
      const verifyPayment = async () => {
        try {
          const response = await axios.post(
            `${base_url}user/order/verifyPhonePePayment`,
            {
              merchantTransactionId: merchantTransactionId,
            },
            config2
          );

          if (response.data.success) {
            const verifiedData = response.data.data;
            const phonePeTransactionId = verifiedData.transactionId; // Official ID from PhonePe response

            // Save Order in Redux Store
            dispatch(
              createAnOrder({
                totalPrice: totalAmount + 100, // Include shipping
                totalPriceAfterDiscount: (appliedDiscount ? Number(appliedDiscount) : totalAmount) + 100,
                orderItems: cartProductState,
                paymentInfo: {
                  orderId: phonePeTransactionId, // Use official PhonePe ID
                  merchantTransactionId: merchantTransactionId,
                },
                shippingInfo: JSON.parse(localStorage.getItem("address")),
              })
            );

            // Clear Cart and Reset State
            dispatch(deleteUserCart(config2));
            localStorage.removeItem("address");
            dispatch(resetState());

            // Redirect to orders page
            navigate("/my-orders");
          } else {
            setPaymentError(response.data.message || "Payment verification failed.");
          }
        } catch (error) {
          console.error("Payment verification error:", error);
          setPaymentError("Error verifying payment.");
        }
      };

      verifyPayment();
    }
  }, [dispatch, navigate, totalAmount, cartProductState, config2]);
  return (
    <Container class1="checkout-wrapper py-5 home-wrapper-2">
      <div className="row">
        <div className="col-7">
          <div className="checkout-left-data">
            <h3 className="website-name">SofTech</h3>
            <nav
              style={{ "--bs-breadcrumb-divider": ">" }}
              aria-label="breadcrumb"
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link className="text-dark total-price" to="/cart">
                    Cart
                  </Link>
                </li>
                &nbsp; /&nbsp;
                <li
                  className="breadcrumb-item total-price active"
                  aria-current="page"
                >
                  Information
                </li>
                &nbsp; /
                <li className="breadcrumb-item total-price active">Shipping</li>
                &nbsp; /
                <li
                  className="breadcrumb-item total-price active"
                  aria-current="page"
                >
                  Payment
                </li>
              </ol>
            </nav>
            <h4 className="title total">Contact Information</h4>
            <p className="user-details total">
              Admin (softechmathikere@gmail.com)
            </p>
            <h4 className="mb-3">Shipping Address</h4>
            <form
              onSubmit={formik.handleSubmit}
              action=""
              className="d-flex gap-15 flex-wrap justify-content-between"
            >
              {/* ... (Form Fields - Same as before) */}
              <div className="w-100">
                <select
                  className="form-control form-select"
                  id=""
                  name="country"
                  value={formik.values.country}
                  onChange={formik.handleChange("country")}
                  onBlur={formik.handleBlur("country")}
                >
                  <option value="" disabled>
                    Select Country
                  </option>
                  <option value="India">India</option>
                </select>
                <div className="error ms-2 my-1">
                  {formik.touched.country && formik.errors.country}
                </div>
              </div>
              <div className="flex-grow-1">
                <input
                  type="text"
                  placeholder="First Name"
                  className="form-control"
                  name="firstname"
                  value={formik.values.firstname}
                  onChange={formik.handleChange("firstname")}
                  onBlur={formik.handleBlur("firstname")}
                />
                <div className="error ms-2 my-1">
                  {formik.touched.firstname && formik.errors.firstname}
                </div>
              </div>
              <div className="flex-grow-1">
                <input
                  type="text"
                  placeholder="Last Name"
                  className="form-control"
                  name="lastname"
                  value={formik.values.lastname}
                  onChange={formik.handleChange("lastname")}
                  onBlur={formik.handleBlur("lastname")}
                />
                <div className="error ms-2 my-1">
                  {formik.touched.lastname && formik.errors.lastname}
                </div>
              </div>
              <div className="w-100">
                <input
                  type="text"
                  placeholder="Address"
                  className="form-control"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange("address")}
                  onBlur={formik.handleBlur("address")}
                />
                <div className="error ms-2 my-1">
                  {formik.touched.address && formik.errors.address}
                </div>
              </div>
              <div className="w-100">
                <input
                  type="text"
                  placeholder="Apartment, Suite ,etc"
                  className="form-control"
                  name="other"
                  value={formik.values.other}
                  onChange={formik.handleChange("other")}
                  onBlur={formik.handleBlur("other")}
                />
              </div>
              <div className="flex-grow-1">
                <input
                  type="text"
                  placeholder="City"
                  className="form-control"
                  name="city"
                  value={formik.values.city}
                  onChange={formik.handleChange("city")}
                  onBlur={formik.handleBlur("city")}
                />
                <div className="error ms-2 my-1">
                  {formik.touched.city && formik.errors.city}
                </div>
              </div>
              <div className="flex-grow-1">
                <select
                  className="form-control form-select"
                  id=""
                  name="state"
                  value={formik.values.state}
                  onChange={formik.handleChange("state")}
                  onBlur={formik.handleBlur("state")}
                >
                  <option value="" disabled>
                    Select State
                  </option>
                  <option value="Bangalore">Bangalore</option>
                </select>
                <div className="error ms-2 my-1">
                  {formik.touched.state && formik.errors.state}
                </div>
              </div>
              <div className="flex-grow-1">
                <input
                  type="text"
                  placeholder="Pincode"
                  className="form-control"
                  name="pincode"
                  value={formik.values.pincode}
                  onChange={formik.handleChange("pincode")}
                  onBlur={formik.handleBlur("pincode")}
                />
                <div className="error ms-2 my-1">
                  {formik.touched.pincode && formik.errors.pincode}
                </div>
              </div>
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center">
                  <Link to="/cart" className="text-dark">
                    <BiArrowBack className="me-2" />
                    Return to Cart
                  </Link>
                  <button className="button" type="submit">
                    Place Order
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div className="col-5">
          <div className="border-bottom py-4">
            {cartState &&
              cartState?.map((item, index) => {
                return (
                  <div
                    key={index}
                    className="d-flex gap-10 mb-2 align-align-items-center"
                  >
                    <div className="w-75 d-flex gap-10">
                      <div className="w-25 position-relative">
                        <span
                          style={{ top: "-10px", right: "2px" }}
                          className="badge bg-secondary text-white rounded-circle p-2 position-absolute"
                        >
                          {item?.quantity}
                        </span>
                        <img
                          src={item?.productId?.images[0]?.url}
                          width={100}
                          height={100}
                          alt="product"
                        />
                      </div>
                      <div>
                        <h5 className="total-price">
                          {item?.productId?.title}
                        </h5>
                        <p className="total-price mb-0">{item?.color?.title}</p>
                        {item?.variants && Object.keys(item.variants).map((key) => (
                          <p key={key} className="mb-0" style={{ fontSize: "12px", color: "#777" }}>
                            {key}: {item.variants[key]}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="total">
                        Rs. {item?.price * item?.quantity}
                      </h5>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="border-bottom py-4">
            <div className="d-flex gap-10 align-items-center mb-3">
              <input
                type="text"
                placeholder="Discount Code"
                className="form-control"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
              />
              <button
                className="button-apply px-3 py-2 text-white bg-dark border-0 rounded"
                type="button"
                onClick={handleApplyCoupon}
                style={{ height: "45px" }}
              >
                Apply
              </button>
            </div>
            {appliedDiscount && (
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="mb-0 total text-success">Discount Applied!</p>
                <p className="mb-0 total-price text-success">- Rs. {(totalAmount - appliedDiscount).toFixed(2)}</p>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center">
              <p className="total">Subtotal</p>
              <p className="total-price">
                Rs. {totalAmount ? totalAmount : "0"}
              </p>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <p className="mb-0 total">Shipping</p>
              <p className="mb-0 total-price">Rs. 100</p>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center border-bootom py-4">
            <h4 className="total">Total</h4>
            <h5 className="total-price">
              Rs. {totalAmount ? (appliedDiscount ? Number(appliedDiscount) : totalAmount) + 100 : "0"}
            </h5>
          </div>
          {paymentError && (
            <div className="text-danger mt-3">{paymentError}</div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default Checkout;
